import "./ReviewContent.css";

import { Dispatch, SetStateAction } from "react";

import { ProjectActivitiesConstants } from "../../../../../../constants";
import { SearchActivityHistoryResponse } from "../../../../../../service/query";
import { Button, Column, CursorDataGrid, Divider, DownloadDropdown, Row } from "../../../../../../widget";
import { CompleteActivityModal, ReturnToAssessorModal, SubmitActivityModal } from "../../../../components";
import { ActivityDefinition } from "../../types";
import { isActivityDataValid } from "../../utils";
import { useReviewContent } from "./useReviewContent";

interface ReviewContentProps {
  isEdit?: boolean;
  projectUuid?: string;
  groupUuid?: string;
  setShowVersionConflictModal?: Dispatch<SetStateAction<boolean>>;
  setDraftActivityHistoryUuid?: Dispatch<SetStateAction<string | undefined>>;
  setNewVersionActivityHistory?: Dispatch<SetStateAction<SearchActivityHistoryResponse | undefined>>;
  activityDefinition: ActivityDefinition;
  handleMoveToStep: (targetStepKeys: string[]) => void;
}

export const ReviewContent = ({
  isEdit = false,
  projectUuid,
  groupUuid,
  setShowVersionConflictModal,
  setDraftActivityHistoryUuid,
  setNewVersionActivityHistory,
  activityDefinition,
  handleMoveToStep,
}: ReviewContentProps): JSX.Element => {
  const {
    activityData,
    columns,
    onChange,
    dataIsLoading,
    definitionData,
    defaultSortingCriteria,
    hasTemplate,
    showReturnToAssessorModal,
    setShowReturnToAssessorModal,
    showSubmitActivityModal,
    setShowSubmitActivityModal,
    showCompleteActivityModal,
    setShowCompleteActivityModal,
    showDownloadActivityFilesModal,
    setShowDownloadActivityFilesModal,
    refreshActivityWizard,
    setRefreshActivityWizard,
    setShowSubmitModalSwitcher,
    exitActivityWizard,
    submitActivity,
    completeActivity,
  } = useReviewContent(
    activityDefinition,
    handleMoveToStep,
    setShowVersionConflictModal,
    setDraftActivityHistoryUuid,
    setNewVersionActivityHistory
  );

  const displayWithOrWithoutSaveForSubmitButton = (): JSX.Element => {
    return activityData?.isDraft ? (
      <Button size="medium" text="Save and Submit" onClick={() => submitActivity(activityData?.activity.status)} />
    ) : (
      <Button size="medium" text="Submit" onClick={() => setShowSubmitModalSwitcher(activityData?.activity.status)} />
    );
  };

  const displayWithOrWithoutSaveForCompleteButton = (): JSX.Element => {
    return activityData?.isDraft ? (
      <Button size="medium" text="Save and Complete" onClick={() => completeActivity()} />
    ) : (
      <Button size="medium" text="Complete" onClick={() => setShowCompleteActivityModal(true)} />
    );
  };
  return (
    <>
      <Row spacingV={isEdit ? "m" : "xl"} className="ActivityReviewHeader">
        <Column span={12}>
          <h1>Review</h1>
        </Column>
      </Row>
      {isEdit && (
        <Row spacingV="xl" className="ActivityReviewHeader">
          <Column span={12}>
            <p className="body1">An activity can only be submitted once all sections are validated and complete</p>
          </Column>
        </Row>
      )}
      {activityData && (
        <div className="ActivityReviewContent">
          <div className="ActivityReviewContentHeader">
            <Row spacingV="l">
              <Column className="ActivityReviewContentTitle" justify="center" align="start" span={6}>
                <h2>{`Version ${activityData.versionNumber} ${activityData.isCurrent ? "(Latest)" : ""}`}</h2>
              </Column>
              <Column span={4} justify="end">
                {isEdit &&
                  activityData.completionPercentage === 1 &&
                  (activityData.activity.status === ProjectActivitiesConstants.STATUS_IN_PROGRESS ||
                    activityData.activity.status === ProjectActivitiesConstants.STATUS_CHANGES_REQUIRED ||
                    activityData.activity.status === ProjectActivitiesConstants.STATUS_REVIEW_CANCELLED) &&
                  isActivityDataValid(activityData?.data.steps) &&
                  (definitionData?.isManuallyApproved
                    ? displayWithOrWithoutSaveForCompleteButton()
                    : displayWithOrWithoutSaveForSubmitButton())}
              </Column>
              {hasTemplate && (
                <Column span={2}>
                  <DownloadDropdown
                    setShowDownloadActivityFilesModal={setShowDownloadActivityFilesModal}
                    showDownloadActivityFilesModal={showDownloadActivityFilesModal}
                    activityHistoryUuid={activityData.uuid}
                    currentOnly={activityData.isCurrent}
                  />
                </Column>
              )}
            </Row>
            <Row spacingV="ll">
              <Column span={2}>
                <h3>Author</h3>
                <p className="body2">{activityData.createdByUser.fullName}</p>
              </Column>
              <Column span={2}>
                <h3>Variant</h3>
                <p className="body2">{activityData.activity.variant ?? "N/A"}</p>
              </Column>
            </Row>
          </div>
          <Row spacingV="ll">
            <Column span={12}>
              <Divider type="horizontal" />
            </Column>
          </Row>
          <Row spacingV="ml">
            <Column span={12}>
              <h3>Activity documents</h3>
            </Column>
          </Row>
          <CursorDataGrid
            columns={columns}
            clientSide={false}
            filterable
            onChange={onChange}
            data={[]}
            noDataMessage="No activity documents to display"
            dataIsLoading={dataIsLoading}
            defaultSortingCriteria={defaultSortingCriteria}
          />
        </div>
      )}
      {showSubmitActivityModal && activityData && activityData?.uuid && (
        <SubmitActivityModal
          show={showSubmitActivityModal}
          onClose={() => setShowSubmitActivityModal(false)}
          activityUuid={activityData.activity.uuid}
          activityStatus={activityData.activity.status}
          activityHistoryUuid={activityData.uuid}
          activityHistoryRowVersion={activityData.rowVersion}
          shouldRefreshActivities={() => setRefreshActivityWizard(!refreshActivityWizard)}
          isDraft={activityData.isDraft}
          exitActivityWizard={exitActivityWizard}
          projectUuid={projectUuid}
          groupUuid={groupUuid}
        />
      )}
      {showReturnToAssessorModal && activityData && activityData?.uuid && (
        <ReturnToAssessorModal
          show={showReturnToAssessorModal}
          onClose={() => setShowReturnToAssessorModal(false)}
          activityUuid={activityData.activity.uuid}
          activityHistoryUuid={activityData.uuid}
          activityHistoryRowVersion={activityData.rowVersion}
          isDraft={activityData.isDraft}
          exitActivityWizard={exitActivityWizard}
        />
      )}
      {activityData && showCompleteActivityModal && (
        <CompleteActivityModal
          show={showCompleteActivityModal}
          onClose={() => setShowCompleteActivityModal(false)}
          activityUuid={activityData.activity.uuid}
          activityProjectUuid={activityData.activity.project?.uuid}
          activityHistoryUuid={activityData.uuid}
          activityHistoryRowVersion={activityData.rowVersion}
          isDraft={activityData.isDraft}
        />
      )}
    </>
  );
};
